import { wencesApi } from "@/api/wencesApi";
import { loadSystemCurrency, logoutAction } from "@/pages/Auth/actions/login.action";
import {
  fetchTranslationOverridesFromApiAndPersist,
  syncI18nLocaleFromUser,
} from "@/i18n";
import {
  EMAIL_SCOPED_FLAG,
  EMAIL_SCOPED_PATH_KEY,
} from "@/utils/emailScopedSession";

/** Parámetro del enlace de acceso de un solo uso que genera un admin. */
export const ADMIN_LOGIN_LINK_PARAM = "ali";

/**
 * Pide un enlace para entrar como ese usuario y devuelve la URL ya armada.
 * El backend manda solo el token: el origin lo pone el navegador, que sabe en qué tenant
 * está parado mejor que cualquier cabecera.
 *
 * @param {string} userId
 * @returns {Promise<{ url: string, expiresAt: string, ttlMinutes: number, user: object }>}
 */
export async function createAdminLoginLink(userId) {
  const { data } = await wencesApi.post(`/api/users/${userId}/login-link`);
  if (!data?.ok || !data?.token) {
    throw new Error(data?.error || "No se pudo generar el enlace");
  }
  const url = `${window.location.origin}/?${ADMIN_LOGIN_LINK_PARAM}=${encodeURIComponent(
    data.token
  )}`;
  return { url, expiresAt: data.expiresAt, ttlMinutes: data.ttlMinutes, user: data.user };
}

function persistImpersonatedLogin(data) {
  const { token, user, role } = data;
  localStorage.setItem("token", token);
  localStorage.setItem("userId", user._id);
  localStorage.setItem("_id", user._id);
  localStorage.setItem("role", role);
  localStorage.setItem("name", user.name || "");
  if (user.restaurant) localStorage.setItem("restaurant", user.restaurant);
  if (user.country) localStorage.setItem("country", user.country);
  // Es una sesión normal, no una acotada por enlace de email.
  localStorage.removeItem(EMAIL_SCOPED_FLAG);
  localStorage.removeItem(EMAIL_SCOPED_PATH_KEY);
  syncI18nLocaleFromUser(user);
}

/** URL absoluta del destino tras el canje: la raíz, con el resto de la query intacta. */
function buildLandingUrl(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v ?? ""));
    else if (value != null) params.append(key, value);
  });
  const search = params.toString();
  return `${window.location.origin}/${search ? `?${search}` : ""}`;
}

/**
 * Si la URL trae ?ali=, lo canjea y deja la sesión de ese usuario.
 * El enlace se quema en el canje, así que hay que sacarlo de la URL sí o sí: si el
 * navegador recarga con el parámetro puesto, el segundo intento ya falla.
 *
 * Devuelve la URL a la que hay que recargar —recargar, no navegar—: ver `reload`.
 *
 * @param {import('vue-router').Route} route
 * @returns {Promise<{ reload: string }|null>}
 */
export async function tryConsumeAdminLoginLink(route) {
  const ali = route?.query?.[ADMIN_LOGIN_LINK_PARAM];
  if (!ali) return null;

  const nextQuery = { ...route.query };
  delete nextQuery[ADMIN_LOGIN_LINK_PARAM];

  const { data } = await wencesApi.post(
    "/api/public/admin-login-link/exchange",
    { ali },
    // Que el enlace no sirva no es motivo para cerrar la sesión que ya había abierta.
    { esCanjeDeEnlace: true }
  );
  if (!data?.ok || !data?.data?.token) {
    throw new Error(data?.error || "Enlace inválido");
  }

  // Entrar como otro usuario reemplaza la sesión que haya en este navegador. Cerrar la
  // anterior antes de abrir la nueva: lo que el admin tenía y el suplantado no
  // (restaurant, país, datos de cliente) se quedaría puesto y pasaría por suyo.
  await logoutAction();
  persistImpersonatedLogin(data.data);
  await loadSystemCurrency(data.data.token);
  await fetchTranslationOverridesFromApiAndPersist();

  // Recarga de verdad, no `router.replace`: el canje ocurre con la app ya arrancada y
  // con las peticiones del admin en vuelo. La de /api/users/myprofile sale al crearse
  // App.vue —con el token del admin— y responde después del canje; queda 2 s en el caché
  // de wencesApi y el layout la lee ahí, así que reescribía `role` con el del admin y el
  // enlace mostraba el menú de admin sobre la sesión del otro usuario. Volver a cargar la
  // página es lo único que deja atrás todo lo que quedó en memoria de la sesión anterior.
  return { reload: buildLandingUrl(nextQuery) };
}
